/**
 * /academy/start — free, self-serve onboarding for Scholar Athlete Academy.
 * Create an account (no payment), then choose a path: SAA Middle School (pick
 * grade 5-8) and/or the L'Chaim 30-day program. Selections drop the coursework
 * straight into the student's portal.
 */
import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  GraduationCap, Sparkles, Loader2, ArrowRight, Check, School, Star,
} from 'lucide-react';
import api from '../services/api';
import { toast } from 'sonner';

const authHeaders = () => ({ Authorization: `Bearer ${localStorage.getItem('academy_token') || ''}` });

const SCHOOL_START = new Date('2026-09-08T00:00:00-07:00');
const daysUntilSchool = () => Math.max(0, Math.ceil((SCHOOL_START - new Date()) / 86400000));

const AcademyStart = () => {
  const navigate = useNavigate();
  const [checking, setChecking] = useState(true);
  const [user, setUser] = useState(null);
  const [mode, setMode] = useState('signup'); // signup | login
  const [form, setForm] = useState({ name: '', email: '', password: '', parent_email: '' });
  const [busy, setBusy] = useState(false);

  const [grade, setGrade] = useState('');
  const [usState, setUsState] = useState('CA');
  const [parentName, setParentName] = useState('');
  const [parentEmail, setParentEmail] = useState('');

  const refreshUser = async () => {
    const token = localStorage.getItem('academy_token');
    if (!token) { setChecking(false); return; }
    try {
      const r = await api.get('/academy/auth/me', { headers: authHeaders() });
      const u = r.data.user || r.data;
      setUser(u);
      if (u?.parent_email) setParentEmail(u.parent_email);
      if (u?.parent_name) setParentName(u.parent_name);
    } catch (e) {
      localStorage.removeItem('academy_token');
    } finally { setChecking(false); }
  };

  useEffect(() => { refreshUser(); }, []);

  const submitAuth = async () => {
    if (!form.email || !form.password || (mode === 'signup' && !form.name)) {
      toast.error('Fill in the required fields'); return;
    }
    if (mode === 'signup' && !form.parent_email) {
      toast.error('Parent email is required'); return;
    }
    setBusy(true);
    try {
      const endpoint = mode === 'signup' ? '/academy/auth/signup' : '/academy/auth/login';
      const payload = mode === 'signup'
        ? { name: form.name, email: form.email, password: form.password, phone: '', parent_email: form.parent_email }
        : { email: form.email, password: form.password };
      const r = await api.post(endpoint, payload);
      localStorage.setItem('academy_token', r.data.token);
      const u = r.data.user;
      setUser(u);
      if (form.parent_email) setParentEmail(form.parent_email);
      if (u.role === 'teacher') { navigate('/teacher-dashboard'); return; }
      toast.success(mode === 'signup' ? 'Account created — choose your path' : 'Welcome back');
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Something went wrong');
    } finally { setBusy(false); }
  };

  const enrollMiddleSchool = async () => {
    if (!grade) { toast.error('Select a grade first'); return; }
    if (!parentEmail) { toast.error('Parent email is required'); return; }
    setBusy(true);
    try {
      await api.post('/academy/enroll/middle-school',
        { grade: Number(grade), us_state: usState, parent_email: parentEmail || null, parent_name: parentName || null },
        { headers: authHeaders() });
      toast.success('Enrolled in SAA Middle School!');
      await refreshUser();
      navigate('/academy/school');
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Could not enroll');
    } finally { setBusy(false); }
  };

  const enrollLchaim = async () => {
    setBusy(true);
    try {
      await api.post('/academy/enroll/lchaim', {}, { headers: authHeaders() });
      toast.success('L\u2019Chaim journey started!');
      await refreshUser();
      navigate('/academy/lchaim/dashboard');
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Could not enroll');
    } finally { setBusy(false); }
  };

  if (checking) {
    return <div className="min-h-screen bg-[#0a0a0a] flex items-center justify-center"><Loader2 className="w-8 h-8 animate-spin text-orange-500" /></div>;
  }

  const enrolledSchool = user?.is_student && user?.grade_level;
  const enrolledLchaim = !!user?.lchaim_tier;

  return (
    <div className="min-h-screen bg-[#0a0a0a] text-white py-14 px-4" data-testid="academy-start">
      <div className="max-w-4xl mx-auto">
        <div className="text-center mb-10">
          <div className="text-[10px] tracking-[0.4em] uppercase text-orange-500 font-black">Scholar Athlete Academy</div>
          <h1 className="mt-3 text-4xl sm:text-5xl font-black uppercase tracking-tight leading-[0.95]">
            {user ? 'Choose Your Path' : 'Create Your Free Account'}
          </h1>
          <p className="text-sm text-zinc-400 mt-3 max-w-lg mx-auto">
            {user
              ? 'No paywall. Pick Middle School, L\u2019Chaim, or both — your work drops straight into your portal.'
              : 'Free to join. Create an account, then choose your track.'}
          </p>
        </div>

        <div className="mb-8 border-2 border-orange-500/40 bg-orange-500/5 px-5 py-4 flex items-center gap-4" data-testid="school-start-banner">
          <div className="w-11 h-11 border-2 border-orange-500 flex items-center justify-center shrink-0">
            <GraduationCap className="w-5 h-5 text-orange-500" />
          </div>
          <div>
            <div className="text-[10px] tracking-[0.3em] uppercase text-orange-500 font-black">School Year Begins September 8, 2026</div>
            <p className="text-sm text-zinc-300 mt-1 leading-relaxed">
              <span className="font-black text-white">{daysUntilSchool()} days</span> until the first day. Enroll now and your <span className="text-orange-400 font-bold">Summer Prep Bridge</span> — self-paced review from the grade below — drops into your portal instantly so you arrive ready.
            </p>
          </div>
        </div>

        {!user ? (
          /* ---- Auth ---- */
          <div className="max-w-md mx-auto border-2 border-zinc-800 p-8" data-testid="academy-start-auth">
            <div className="flex gap-2 mb-6">
              <button onClick={() => setMode('signup')} className={`flex-1 py-2 text-[11px] font-black uppercase tracking-[0.2em] border-2 transition-colors ${mode === 'signup' ? 'bg-orange-500 border-orange-500 text-black' : 'border-zinc-800 text-zinc-400'}`} data-testid="start-mode-signup">Create Account</button>
              <button onClick={() => setMode('login')} className={`flex-1 py-2 text-[11px] font-black uppercase tracking-[0.2em] border-2 transition-colors ${mode === 'login' ? 'bg-orange-500 border-orange-500 text-black' : 'border-zinc-800 text-zinc-400'}`} data-testid="start-mode-login">Sign In</button>
            </div>
            <div className="space-y-3">
              {mode === 'signup' && (
                <input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} placeholder="Student full name" className="w-full bg-black border-2 border-zinc-800 px-3 py-3 text-sm focus:border-orange-500 outline-none" data-testid="start-name" />
              )}
              <input value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} type="email" placeholder="Email" className="w-full bg-black border-2 border-zinc-800 px-3 py-3 text-sm focus:border-orange-500 outline-none" data-testid="start-email" />
              <input value={form.password} onChange={(e) => setForm({ ...form, password: e.target.value })} type="password" placeholder="Password" onKeyDown={(e) => e.key === 'Enter' && submitAuth()} className="w-full bg-black border-2 border-zinc-800 px-3 py-3 text-sm focus:border-orange-500 outline-none" data-testid="start-password" />
              {mode === 'signup' && (
                <input value={form.parent_email} onChange={(e) => setForm({ ...form, parent_email: e.target.value })} type="email" placeholder="Parent email (required)" className="w-full bg-black border-2 border-zinc-800 px-3 py-3 text-sm focus:border-orange-500 outline-none" data-testid="start-parent-email" />
              )}
              <button onClick={submitAuth} disabled={busy} className="w-full bg-orange-500 hover:bg-orange-400 disabled:bg-zinc-800 text-black py-4 font-black text-xs tracking-[0.25em] uppercase transition-colors flex items-center justify-center gap-2" data-testid="start-submit">
                {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : (mode === 'signup' ? 'Create Free Account' : 'Sign In')}
              </button>
            </div>
          </div>
        ) : (
          /* ---- Path selection ---- */
          <div className="grid md:grid-cols-2 gap-5">
            {/* SAA Middle School */}
            <div className="border-2 border-zinc-800 p-6 flex flex-col" data-testid="path-middle-school">
              <div className="w-12 h-12 border-2 border-orange-500 flex items-center justify-center mb-4">
                <School className="w-6 h-6 text-orange-500" />
              </div>
              <h2 className="text-2xl font-black uppercase tracking-tight">SAA Middle School</h2>
              <p className="text-xs text-zinc-400 mt-2 leading-relaxed flex-1">
                Six CA-standard-aligned courses built for student-athletes. Pick your grade and your coursework loads into your portal instantly.
              </p>

              {enrolledSchool ? (
                <div className="mt-5">
                  <div className="flex items-center gap-2 text-emerald-400 text-sm font-bold mb-3" data-testid="school-enrolled">
                    <Check className="w-4 h-4" /> Enrolled · Grade {user.grade_level}
                  </div>
                  <button onClick={() => navigate('/academy/school')} className="w-full bg-orange-500 hover:bg-orange-400 text-black py-3.5 font-black text-xs tracking-[0.25em] uppercase flex items-center justify-center gap-2" data-testid="go-to-portal">
                    Go to Portal <ArrowRight className="w-4 h-4" />
                  </button>
                </div>
              ) : (
                <div className="mt-5 space-y-3">
                  <div>
                    <label className="text-[10px] uppercase tracking-widest text-zinc-500 font-black">Select Grade</label>
                    <div className="grid grid-cols-6 gap-2 mt-2">
                      {[4, 5, 6, 7, 8, 9].map((g) => (
                        <button key={g} onClick={() => setGrade(String(g))} className={`py-3 border-2 font-black text-sm transition-colors ${String(g) === grade ? 'bg-orange-500 border-orange-500 text-black' : 'border-zinc-800 text-zinc-400 hover:border-orange-500'}`} data-testid={`grade-${g}`}>{g}</button>
                      ))}
                    </div>
                  </div>
                  <div>
                    <label className="text-[10px] uppercase tracking-widest text-zinc-500 font-black">Your State (aligns your standards)</label>
                    <select value={usState} onChange={(e) => setUsState(e.target.value)} className="w-full mt-2 bg-black border-2 border-zinc-800 px-3 py-2.5 text-sm text-white focus:border-orange-500 outline-none" data-testid="ms-state-select">
                      <option value="CA">California — Common Core & NGSS</option>
                      <option value="FL">Florida — B.E.S.T. Standards</option>
                      <option value="TX">Texas — TEKS</option>
                    </select>
                    <p className="text-[10px] text-zinc-500 mt-1.5">Your daily practice in The Combine anchors on your state's standards.</p>
                  </div>
                  <input value={parentName} onChange={(e) => setParentName(e.target.value)} placeholder="Parent name (optional)" className="w-full bg-black border-2 border-zinc-800 px-3 py-2.5 text-sm focus:border-orange-500 outline-none" data-testid="ms-parent-name" />
                  <input value={parentEmail} onChange={(e) => setParentEmail(e.target.value)} type="email" placeholder="Parent email (required — daily plan + prep)" className="w-full bg-black border-2 border-zinc-800 px-3 py-2.5 text-sm focus:border-orange-500 outline-none" data-testid="ms-parent-email" />
                  <button onClick={enrollMiddleSchool} disabled={busy} className="w-full bg-orange-500 hover:bg-orange-400 disabled:bg-zinc-800 text-black py-3.5 font-black text-xs tracking-[0.25em] uppercase flex items-center justify-center gap-2" data-testid="enroll-middle-school">
                    {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <>Enroll Free <ArrowRight className="w-4 h-4" /></>}
                  </button>
                </div>
              )}
            </div>

            {/* L'Chaim */}
            <div className="border-2 border-zinc-800 p-6 flex flex-col" data-testid="path-lchaim">
              <div className="w-12 h-12 border-2 border-amber-400 flex items-center justify-center mb-4">
                <Star className="w-6 h-6 text-amber-400" />
              </div>
              <h2 className="text-2xl font-black uppercase tracking-tight">L&rsquo;Chaim 30-Day</h2>
              <p className="text-xs text-zinc-400 mt-2 leading-relaxed flex-1">
                A daily character &amp; faith journey — reflections, journaling, and milestones that build discipline off the field.
              </p>

              {enrolledLchaim ? (
                <div className="mt-5">
                  <div className="flex items-center gap-2 text-emerald-400 text-sm font-bold mb-3" data-testid="lchaim-enrolled">
                    <Check className="w-4 h-4" /> Journey active
                  </div>
                  <button onClick={() => navigate('/academy/lchaim/dashboard')} className="w-full bg-amber-400 hover:bg-amber-300 text-black py-3.5 font-black text-xs tracking-[0.25em] uppercase flex items-center justify-center gap-2" data-testid="open-lchaim">
                    Open L&rsquo;Chaim <ArrowRight className="w-4 h-4" />
                  </button>
                </div>
              ) : (
                <button onClick={enrollLchaim} disabled={busy} className="mt-5 w-full bg-amber-400 hover:bg-amber-300 disabled:bg-zinc-800 text-black py-3.5 font-black text-xs tracking-[0.25em] uppercase flex items-center justify-center gap-2" data-testid="enroll-lchaim">
                  {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <><Sparkles className="w-4 h-4" /> Start Free</>}
                </button>
              )}
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default AcademyStart;
